import { useCallback, useEffect, useRef, useState } from 'react';

const BACKOFF_THRESHOLD = 3;   // attempts before soft lockout
const LOCKOUT_THRESHOLD = 5;   // attempts before full lockout
const BACKOFF_DURATION_S = 10; // seconds
const LOCKOUT_DURATION_S = 30; // seconds

export interface LoginRateLimitState {
  /** True when 3–4 failures: button disabled, countdown running. */
  isBackoff: boolean;
  /** True when 5+ failures: full hard lock, show CAPTCHA / lock message. */
  isLocked: boolean;
  /** Seconds remaining in the current penalty. */
  countdown: number;
  /** Derived convenience flag: !isBackoff && !isLocked */
  canSubmit: boolean;
  /** Number of consecutive failures since last success or page load. */
  failedAttempts: number;
  /** Call on every failed login attempt. */
  recordFailure: () => void;
  /** Call on successful login — resets all state. */
  recordSuccess: () => void;
}

/**
 * Client-side login rate limiter.
 *
 * State is volatile (useState, not localStorage) — resets on page refresh.
 * Uses a future-timestamp for the penalty deadline so tab-backgrounding or
 * React StrictMode double-effects don't corrupt the countdown.
 */
export const useLoginRateLimit = (): LoginRateLimitState => {
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [penaltyEndsAt, setPenaltyEndsAt] = useState<number | null>(null);
  const [countdown, setCountdown] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Drive the countdown ticker off penaltyEndsAt
  useEffect(() => {
    if (penaltyEndsAt === null) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    intervalRef.current = setInterval(() => {
      const remaining = Math.ceil((penaltyEndsAt - Date.now()) / 1000);
      if (remaining <= 0) {
        setCountdown(0);
        setPenaltyEndsAt(null);
      } else {
        setCountdown(remaining);
      }
    }, 100);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [penaltyEndsAt]);

  const recordFailure = useCallback(() => {
    setFailedAttempts((prev) => {
      const next = prev + 1;
      let durationS = 0;
      if (next >= LOCKOUT_THRESHOLD) durationS = LOCKOUT_DURATION_S;
      else if (next >= BACKOFF_THRESHOLD) durationS = BACKOFF_DURATION_S;
      if (durationS > 0) {
        setPenaltyEndsAt(Date.now() + durationS * 1000);
        setCountdown(durationS);
      }
      return next;
    });
  }, []);

  const recordSuccess = useCallback(() => {
    setFailedAttempts(0);
    setPenaltyEndsAt(null);
    setCountdown(0);
  }, []);

  const isLocked = failedAttempts >= LOCKOUT_THRESHOLD && countdown > 0;
  const isBackoff = failedAttempts >= BACKOFF_THRESHOLD && !isLocked && countdown > 0;

  return {
    isBackoff,
    isLocked,
    countdown,
    canSubmit: !isBackoff && !isLocked,
    failedAttempts,
    recordFailure,
    recordSuccess,
  };
};
